import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import SEO, { BreadcrumbComponent } from '../SEO'
import { generateTreatySchema } from '../../services/schemaGenerators'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import MailRoundedIcon from '@mui/icons-material/MailRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import ReportRoundedIcon from '@mui/icons-material/ReportRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import { activityTracker } from '../../services/ActivityTracker'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SortByAlphaRoundedIcon from '@mui/icons-material/SortByAlphaRounded'
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded'
import { ShareRounded } from '@mui/icons-material'
import { Enhanced404Handler } from '../Error404'

import {
  Box,
  IconButton,
  Typography,
  Paper,
  Skeleton,
  Fab,
  Link,
  Zoom,
  useTheme,
  useMediaQuery,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material'
import RelatedDocuments from '../RelatedDocuments'
import LastUpdatedDisplay from '../LastUpdatedDisplay.js'
import { convertLocalDateTimeToISO } from '../../utils/dateUtils'
import {
  handleEmail,
  handleOpenInNewTab,
} from '../../services/articleCardHandlers'
import {
  parseHtmlSanitizeAddTargetToLinks,
} from '../../services/utils'
import {
  extractCountryFromSlug,
  hasSubscriptionAccessToCountry,
} from '../../utils/quotaAccessUtils'
import UpgradePromptModal from '../monetization/UpgradePromptModal'
import { useAuth } from '../../context/AuthContext'
import apiService from '../../services/ApiService'
import { AnnotatedArticle } from '../annotations/EnhancedAnnotatedArticle.js'
import FeedbackForm from '../FeedbackForm'
import UnofficialTranslationNotice from '../UnofficialTranslationNotice'
import BetaVersionNotice from '../BetaVersionNotice'
import { createSortButtonStyles } from '../../styles/sortButtonDefinitionTableStyles.js'
// Module-level cache
const contentCache = {}
const inFlightTreatyRequests = {}

const TaxTreatyArticle = ({
  treaty,
  searchQuery,
  showSnackbar,
  isInModal = false, // Prop to indicate if component is inside a modal
  parentContainerRef = null, // Reference to parent container for scrolling in modal mode
}) => {
  const [treatyContent, setTreatyContent] = useState(null)
  const { quotaStatus } = useAuth()
  const [accessBlocked, setAccessBlocked] = useState(false)
  const [quotaError, setQuotaError] = useState(null)
  const [loading, setLoading] = useState(true) // The loading state if initially set to false, then set to true when fetching begins. Be aware it might briefly show "No content available" before showing the loading skeleton.
  const [error, setError] = useState(null)
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const componentRef = useRef()
  const printContentRef = useRef()
  const fetchedRef = useRef(false) // Track fetch state to prevent duplicate calls
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [bookmarkLoading, setBookmarkLoading] = useState(false)
  // we need to sort lexicographically, all the definitions of Article 1 of CIT law
  const [parsedContent, setParsedContent] = useState(null)
  const [sortedContent, setSortedContent] = useState(null)
  const [contentHtml, setContentHtml] = useState(null)
  const [gtlNotesContent, setGtlNotesContent] = useState(null)
  const [gtlNotesTitle, setGtlNotesTitle] = useState('GTL Notes')
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false)
  // const [isUnofficialTranslation, setIsUnofficialTranslation] = useState(false)

  const themeColors = useMemo(() => ({
    primary: '#232536',
    secondary: '#ffcf51',
    hover: '#3a3e5a',
  }), [])
  // Create the sort button styles
  const sortButtonStyles = useMemo(
    () => createSortButtonStyles(themeColors),
    [themeColors]
  )
  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const buttonBorderRadius = '28px'
  const currentDocumentCountry = useMemo(() => {
    const slug = treatyContent?.slug || treaty?.slug || ''
    return extractCountryFromSlug(slug)
  }, [treatyContent?.slug, treaty?.slug])
  const canModifyDocumentFeatures = useMemo(() => {
    if (!quotaStatus) return true

    const hasSubscriptionAccess = hasSubscriptionAccessToCountry(
      quotaStatus,
      currentDocumentCountry,
      { requireDtaaAccess: true },
    )
    const remainingDtaaViews = quotaStatus.remainingDtaaViews ?? 0
    const creditsBalance = quotaStatus.creditsBalance ?? 0

    return hasSubscriptionAccess || remainingDtaaViews > 0 || creditsBalance > 0
  }, [quotaStatus, currentDocumentCountry])

  // const baseUrl = process.env.REACT_APP_BACKEND_URL

  // Decide which container to use for scroll detection and scrollToTop action
  const scrollContainer = isInModal ? parentContainerRef : window
  const checkBookmarkStatus = useCallback(async () => {
    if (!treatyContent?.slug) return
    try {
      const bookmarkResponse = await apiService.getBookmarkByArticle(
        treatyContent.slug
      )
      if (
        bookmarkResponse &&
        bookmarkResponse.articleSlug === treatyContent.slug
      ) {
        setIsBookmarked(true)
        // console.log('Bookmark found:', bookmarkResponse)
      } else {
        setIsBookmarked(false)
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Article is not bookmarked
        setIsBookmarked(false)
      } else if (error.response?.status === 401) {
        // User not authenticated
        console.warn('User not authenticated for bookmark check')
        setIsBookmarked(false)
      } else {
        // Network or server error
        console.error('Failed to check bookmark status:', error)
        setIsBookmarked(false)
      }
    }
  }, [treatyContent?.slug])
  useEffect(() => {
    if (treatyContent?.slug) {
      checkBookmarkStatus()
      // Track article view if not in modal
      if (!isInModal) {
        activityTracker.trackDocumentView(
          'DTAA',
          treatyContent.slug,
          treatyContent.title?.replace(/<[^>]*>/g, ''), // Remove HTML tags
          {
            number: treatyContent.number,
            // pathString: treatyContent.pathString,
            // lawId: treatyContent.slug.split('-article-')[0], // Extract law from slug
          }
        )
      }
    }
  }, [treatyContent, isInModal, checkBookmarkStatus])
  // Show back to top button when scrolled down
  useEffect(() => {
    if (!scrollContainer) return
    const modalScrollContainer =
      isInModal && scrollContainer?.current ? scrollContainer.current : null

    const handleScroll = () => {
      // For window, use scrollY; for element, use scrollTop
      const scrollPosition = isInModal
        ? modalScrollContainer?.scrollTop
        : window.scrollY

      if (scrollPosition > 300) {
        setShowBackToTop(true)
      } else {
        setShowBackToTop(false)
      }
    }

    // Add scroll listener to the appropriate container
    if (modalScrollContainer) {
      modalScrollContainer.addEventListener('scroll', handleScroll)
    } else {
      window.addEventListener('scroll', handleScroll)
    }

    return () => {
      // Clean up scroll listener
      if (modalScrollContainer) {
        modalScrollContainer.removeEventListener('scroll', handleScroll)
      } else {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [isInModal, scrollContainer])

  const handleBookmark = async () => {
    if (bookmarkLoading) return
    if (!isBookmarked && !canModifyDocumentFeatures) {
      showSnackbar?.(
        'This document is in view-only mode. Upgrade or wait for quota reset to add bookmarks.',
        'warning',
      )
      return
    }

    setBookmarkLoading(true)
    try {
      if (isBookmarked) {
        // Remove bookmark
        await apiService.deleteBookmarkByArticle(treatyContent.slug)
        setIsBookmarked(false)

        // Track bookmark removal
        activityTracker.trackDocumentAction(
          'unbookmark',
          'DTAA',
          treatyContent.slug,
          treatyContent.title?.replace(/<[^>]*>/g, '')
        )

        showSnackbar?.('Bookmark removed', 'info')
      } else {
        // Add bookmark
        const bookmarkData = {
          articleSlug: treatyContent.slug,
          title:
            treatyContent.title?.replace(/<[^>]*>/g, '') || 'Untitled Article',
          url: `/tax-treaties/${treatyContent.slug}`,
          category: 'DTAA',
          description:
            treatyContent.summary ||
            (treatyContent.textOnly
              ? treatyContent.textOnly.substring(0, 200) + '...'
              : ''),
          tags: treatyContent.tags || [],
        }

        await apiService.createBookmark(bookmarkData)
        setIsBookmarked(true)

        // Track bookmark addition
        activityTracker.trackDocumentAction(
          'bookmark',
          'DTAA',
          treatyContent.slug,
          treatyContent.title?.replace(/<[^>]*>/g, '')
        )

        showSnackbar?.('Article bookmarked!', 'success')
      }
    } catch (error) {
      console.error('Bookmark error:', error)
      const errorMessage =
        error.response?.data?.message || 'Failed to update bookmark'
      showSnackbar?.(errorMessage, 'error')
    } finally {
      setBookmarkLoading(false)
    }
  }
  // Scroll to top function
  const scrollToTop = useCallback(() => {
    if (isInModal && parentContainerRef?.current) {
      parentContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [isInModal, parentContainerRef])

  const extractGtlNotes = useCallback((htmlContent) => {
    try {
      if (!htmlContent) return null
      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlContent, 'text/html')
      // Debug: log all h3.linktogtl elements
      const allGtlHeadings = doc.querySelectorAll('h3.linktogtl')
      // Look for the GTL Notes heading (not the "See GTL Notes" one)
      let gtlHeading = null
      for (const heading of allGtlHeadings) {
        const anchorTag = heading.querySelector('a')
        if (anchorTag) {
          const text = anchorTag.textContent.trim()
          const id = anchorTag.id
          // Look for the actual GTL Notes heading (usually has id="bookmark" and text starts with "GTL Notes")
          if (text.startsWith('GTL Notes') && id === 'bookmark') {
            gtlHeading = heading
            break
          }

          // Alternative: look for heading that doesn't contain brackets (not "[See GTL Notes]")
          else if (
            text.includes('GTL Notes') &&
            !text.includes('[') &&
            !text.includes(']')
          ) {
            gtlHeading = heading

            break
          }
        }
      }
      if (!gtlHeading) {
        // console.log('No GTL Notes heading found')
        return null
      }
      // Get the title from the anchor tag
      const anchorTag = gtlHeading.querySelector('a')
      const title = anchorTag ? anchorTag.textContent.trim() : 'GTL Notes'
      // Find all content after the GTL Notes heading
      let currentElement = gtlHeading.nextElementSibling
      const gtlContent = []
      // Get all following sibling elements until we hit another h3 or end of container
      while (currentElement) {
        if (currentElement.tagName === 'H3') {
          break
        }
        gtlContent.push(currentElement.outerHTML)
        currentElement = currentElement.nextElementSibling
      }
      return gtlContent.length > 0
        ? {
            title,
            content: gtlContent.join(''),
          }
        : null
    } catch (error) {
      console.warn('Error extracting GTL Notes:', error)
      return null
    }
  }, [])

  const removeGtlNotesFromContent = useCallback((htmlContent) => {
    if (!htmlContent) return htmlContent
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    // Find the GTL Notes heading (same logic as extraction)
    const allGtlHeadings = doc.querySelectorAll('h3.linktogtl')
    let gtlHeading = null
    for (const heading of allGtlHeadings) {
      const anchorTag = heading.querySelector('a')
      if (anchorTag) {
        const text = anchorTag.textContent.trim()
        const id = anchorTag.id
        // Look for the actual GTL Notes heading
        if (text.startsWith('GTL Notes') && id === 'bookmark') {
          gtlHeading = heading
          break
        } else if (
          text.includes('GTL Notes') &&
          !text.includes('[') &&
          !text.includes(']')
        ) {
          gtlHeading = heading
          break
        }
      }
    }

    if (gtlHeading) {
      const elementsToRemove = [gtlHeading]
      // Remove all elements following the GTL Notes heading until next h3 or end
      let currentElement = gtlHeading.nextElementSibling
      while (currentElement) {
        if (currentElement.tagName === 'H3') {
          break
        }
        elementsToRemove.push(currentElement)
        currentElement = currentElement.nextElementSibling
      }
      // Remove all collected elements
      elementsToRemove.forEach((el) => el.remove())
    }
    return doc.body.innerHTML
  }, [])

  const memoizedGtlNotes = useMemo(() => {
    return treatyContent?.content
      ? extractGtlNotes(treatyContent.content)
      : null
  }, [treatyContent?.content, extractGtlNotes])

  // * Here we are creating dynamic link tag for this particular component
  // Add CSS styles specific to tax treaties
  useEffect(() => {
    const environment = process.env.REACT_APP_ENVIRONMENT || 'dev'
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `https://gtlcdn-eufeh8ffbvbvacgf.z03.azurefd.net/guide/stylesheets/${environment}/dtaa.css`
    link.id = 'tax-treaty-css'

    if (!document.getElementById('tax-treaty-css')) {
      document.head.appendChild(link)
    }

    return () => {
      document.getElementById('tax-treaty-css')?.remove() // Cleanup on unmount
    }
  }, [])
  // SEO Data Generation
  const generateSEOData = useCallback(() => {
    if (!treatyContent) return null

    // Extract country information
    const country2Name = treatyContent?.country2Name || 'Unknown Country'
    const translationStatus = treatyContent?.officialTranslation
      ? 'Official'
      : 'Unofficial'

    // Generate title based on treaty data
    const seoTitle =
      treatyContent.metaTitle ||
      `${treatyContent?.country1Name}-${country2Name} Tax Treaty | DTAA | ${translationStatus} Translation | GCC Tax Laws`

    // Generate description from content
    const seoDescription =
      treatyContent.metaDescription ||
      treatyContent?.summary ||
      (treatyContent?.textOnly
        ? treatyContent?.textOnly.substring(0, 155) + '...'
        : `Double Taxation Avoidance Agreement between ${treatyContent?.country1Name} and ${country2Name}. ${translationStatus} translation.`)

    // Generate canonical URL
    const canonicalUrl = `https://gcctaxlaws.com/tax-treaties/${treatyContent.slug}`

    // Generate keywords
    const keywords =
      treatyContent.metaKeywords ||
      [
        `${treatyContent?.country1Name}-${country2Name} tax treaty`,
        'DTAA',
        'double taxation agreement',
        `${country2Name.toLowerCase()} treaty`,
        `${treatyContent?.country1Name} ${country2Name} tax treaty`,
        translationStatus.toLowerCase() + ' translation',
        'international tax',
      ]
        .filter(Boolean)
        .join(', ')

    // Generate schema markup
    const schemaMarkup = generateTreatySchema(treatyContent)
    const modifiedDate = convertLocalDateTimeToISO(treatyContent.updatedAt)
    const publishedDate = convertLocalDateTimeToISO(treatyContent.updatedAt)
    return {
      title: seoTitle,
      description: seoDescription,
      canonical: canonicalUrl,
      keywords,
      schemaMarkup,
      publishedDate,
      modifiedDate,
      country2Name,
      translationStatus,
      issueDate: treatyContent?.issueDate,
    }
  }, [treatyContent])

  useEffect(() => {
    let isMounted = true
    // Fetch tax treaty content
    const getTreatyContent = async () => {
      // Skip if no slug or already fetching
      if (treaty?.content) {
        if (!isMounted) return
        setTreatyContent(treaty)
        setAccessBlocked(false)
        setQuotaError(null)
        setError(null)
        setLoading(false)
        fetchedRef.current = false
        return
      }

      if (!treaty?.slug || fetchedRef.current) {
        return
      }

      // Keep blocked state stable until quota/subscription changes.
      if (accessBlocked && !canModifyDocumentFeatures) {
        if (isMounted) {
          setLoading(false)
        }
        return
      }

      const shouldForceFreshFetch =
        accessBlocked && canModifyDocumentFeatures
      if (shouldForceFreshFetch) {
        delete contentCache[treaty.slug]
        delete inFlightTreatyRequests[treaty.slug]
      }

      // Check module-level cache first
      if (contentCache[treaty.slug]) {
        const cached = contentCache[treaty.slug]
        if (!cached.blocked) {
          if (!isMounted) return
          setTreatyContent(cached)
          setAccessBlocked(false)
          setQuotaError(null)
          setError(null)
          setLoading(false)
          return
        }
        // Never trust stale blocked cache; refetch from backend.
        delete contentCache[treaty.slug]
      }

      if (isMounted) {
        setLoading(true)
        setQuotaError(null)
        setError(null)
      }
      fetchedRef.current = true
      try {
        const requestOptions = shouldForceFreshFetch
          ? { bypassCache: true, skipCache: true }
          : {}

        // const data = await apiService.getTaxTreatyBySlug(treaty.slug)
        if (!inFlightTreatyRequests[treaty.slug]) {
          inFlightTreatyRequests[treaty.slug] =
            apiService.getWithQuotaRefresh(
              `/api/v1/tax-treaties/slug/${treaty.slug}`,
              {},
              requestOptions,
            )
        }

        const data = await inFlightTreatyRequests[treaty.slug]
        if (!isMounted) return
        // Store in module-level cache
        contentCache[treaty.slug] = data
        setTreatyContent(data)
        setError(null)
        setAccessBlocked(false)
        setQuotaError(null)

        // Refresh quota after successful access
        // setTimeout(() => refreshQuota(), 500)
        // refreshQuota().catch((err) => // Backend already updated quota
        //   console.warn('Failed to refresh quota:', err)
        // )
      } catch (err) {
        if (!isMounted) return
        console.error('Error fetching tax treaty content:', err.message)
        // Handle 402 - Access Blocked
        if (err.response?.status === 402) {
          setAccessBlocked(true)
          setError(null)
          // Use quotaData if available (set by ApiService.handleError)
          setQuotaError(
            err.isQuotaError && err.quotaData ? err.quotaData : null,
          )

          // Cache the blocked state
          // Do not cache blocked states, otherwise users can get stuck after quota reset/upgrade.

          if (showSnackbar) {
            const message =
              err.response.data?.message ||
              err.userMessage ||
              'DTAA access blocked: You have exhausted your quota'
            showSnackbar(message, 'error')
          }
        } else {
          setError(err.userMessage || 'Failed to load treaty')
        }
      } finally {
        fetchedRef.current = false
        if (treaty?.slug) {
          delete inFlightTreatyRequests[treaty.slug]
        }
        if (isMounted) {
          setLoading(false)
        }
      }
    }
    getTreatyContent()
    return () => {
      isMounted = false
      fetchedRef.current = false
    }
  }, [
    accessBlocked,
    canModifyDocumentFeatures,
    showSnackbar,
    treaty,
    treaty?.content,
    treaty?.slug,
  ])

  // Generate SEO data
  const seoData = generateSEOData()

  // Parse HTML content when article content changes, so we can check whether it contains any tables or not, if yes then we can show sort button
  useEffect(() => {
    if (treatyContent?.content) {
      // Parse the HTML content
      const parser = new DOMParser()
      const doc = parser.parseFromString(treatyContent.content, 'text/html')
      setParsedContent(doc)
      setSortedContent(null) // Reset sorted content when article changes

      // Extract GTL Notes
      if (memoizedGtlNotes) {
        setGtlNotesContent(memoizedGtlNotes.content)
        setGtlNotesTitle(memoizedGtlNotes.title)
        // Remove GTL Notes from main content
        const contentWithoutGtl = removeGtlNotesFromContent(
          treatyContent.content
        )
        setContentHtml(contentWithoutGtl)
      } else {
        setGtlNotesContent(null)
        setContentHtml(treatyContent.content)
      }
    }
  }, [treatyContent?.content, removeGtlNotesFromContent, memoizedGtlNotes])

  // below function is used to sort all the tables, we just need to sort the table where definitions are there in the article content
  const sortDefinitions = useCallback(() => {
    if (!parsedContent) return

    // Create a deep clone of the parsed content (without GTL Notes)
    const baseContent = memoizedGtlNotes
      ? removeGtlNotesFromContent(treatyContent?.content || '')
      : treatyContent?.content || ''
    const parser = new DOMParser()
    const doc = parser.parseFromString(baseContent, 'text/html')
    const docClone = doc.cloneNode(true)

    // Find all tables in the document
    // const tables = docClone.querySelectorAll('table')

    // Find only tables with class="definition"
    const tables = docClone.querySelectorAll('table.definition')

    tables.forEach((table) => {
      // Get all rows except header row (if any)
      // const rows = Array.from(table.querySelectorAll('tr')).slice(1)

      // Get all rows for sorting including header row
      const rows = Array.from(table.querySelectorAll('tr'))

      if (rows.length > 0) {
        // Sort rows by the text in the first column
        rows.sort((a, b) => {
          const aText = a.querySelector('td:first-child')?.textContent || ''
          const bText = b.querySelector('td:first-child')?.textContent || ''
          return aText.localeCompare(bText)
        })

        // Remove existing rows
        rows.forEach((row) => row.parentNode.removeChild(row))

        // Append sorted rows
        const tbody = table.querySelector('tbody') || table
        rows.forEach((row) => tbody.appendChild(row))
      }
    })

    setSortedContent(docClone)
    setContentHtml(docClone.body.innerHTML)

    if (showSnackbar) {
      showSnackbar('Definitions sorted alphabetically', 'success')
    }
  }, [
    parsedContent,
    showSnackbar,
    memoizedGtlNotes,
    removeGtlNotesFromContent,
    treatyContent,
  ])

  // Reset to original content
  const resetToDefault = useCallback(() => {
    setSortedContent(null)
    // Reset to content without GTL Notes
    const baseContent = memoizedGtlNotes
      ? removeGtlNotesFromContent(treatyContent?.content || '')
      : treatyContent?.content || ''
    setContentHtml(baseContent)
    // Provide feedback when resetting
    if (showSnackbar) {
      showSnackbar('Restored original order', 'info')
    }
  }, [treatyContent, showSnackbar, memoizedGtlNotes, removeGtlNotesFromContent])

  const handleFeedbackFormToggle = useCallback(() => {
    setIsFeedbackFormOpen((prev) => !prev)
  }, [])
  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/tax-treaties/${
      treatyContent?.slug || treaty?.slug
    }`
    const title = treatyContent?.title || treaty?.title || 'Treaty'

    // Remove HTML tags from title for clean sharing
    const cleanTitle = title.replace(/<[^>]*>/g, '')

    const shareData = {
      title: cleanTitle,
      text: `Check out this Treaty: ${cleanTitle}`,
      url: url,
    }

    try {
      let shareMethod = 'shared via link' // default
      // Check if Web Share API is supported (mainly mobile)
      if (navigator.share) {
        shareMethod = 'shared via native api'
        await navigator.share(shareData)
        if (showSnackbar) {
          showSnackbar('Treaty shared successfully! 📤', 'success')
        }
      } else {
        // Fallback to clipboard copy
        await navigator.clipboard.writeText(url)
        if (showSnackbar) {
          showSnackbar('Treaty link copied to clipboard! 📋', 'success')
        }
      }
      activityTracker.trackDocumentAction(
        shareMethod,
        'DTAA',
        treatyContent?.slug || treaty?.slug,
        cleanTitle
      )
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Fallback to clipboard if share fails
        try {
          await navigator.clipboard.writeText(url)
          if (showSnackbar) {
            showSnackbar('Treaty link copied to clipboard! 📋', 'success')
          }
        } catch (clipboardError) {
          if (showSnackbar) {
            showSnackbar('Failed to share treaty', 'error')
          }
          activityTracker.trackDocumentAction(
            'shared via link',
            'DTAA',
            treatyContent?.slug || treaty?.slug,
            cleanTitle,
          )
        }
      }
    }
  }, [treatyContent, treaty, showSnackbar])
  const handlePrint = useCallback(() => {
    if (componentRef.current) {
      const wasAccordionExpanded = isAccordionExpanded

      // If GTL Notes exist and accordion is closed, expand it
      if (gtlNotesContent && !isAccordionExpanded) {
        setIsAccordionExpanded(true)
        // Wait for accordion to expand before printing
        setTimeout(() => {
          performPrint()
          // Restore original accordion state after printing
          setIsAccordionExpanded(wasAccordionExpanded)
        }, 300) // Give accordion time to expand
      } else {
        // No GTL Notes or accordion already expanded, print immediately
        performPrint()
      }
    }

    function performPrint() {
      const printWindow = window.open('', '_blank')
      const sourceNode = printContentRef.current || componentRef.current
      if (!printWindow || !sourceNode) return

      const printDoc = printWindow.document
      printDoc.title = 'GCC Tax Laws - Tax Treaties'
      printDoc.body.replaceChildren()

      // document.styleSheets enumerates every active sheet;
      const styleLoadPromises = []
      const seenHrefs = new Set()
      Array.from(document.styleSheets).forEach((sheet) => {
        try {
          if (sheet.disabled) return
          if (sheet.href) {
            if (seenHrefs.has(sheet.href)) return
            seenHrefs.add(sheet.href)
            const link = printDoc.createElement('link')
            link.rel = 'stylesheet'
            link.type = 'text/css'
            link.href = sheet.href
            const loaded = new Promise((resolve) => {
              link.onload = resolve
              link.onerror = resolve
              setTimeout(resolve, 1500)
            })
            styleLoadPromises.push(loaded)
            printDoc.head.appendChild(link)
          } else {
            const styleEl = printDoc.createElement('style')
            if (sheet.cssRules && sheet.cssRules.length > 0) {
              styleEl.textContent = Array.from(sheet.cssRules)
                .map((rule) => rule.cssText)
                .join('\n')
            } else if (sheet.ownerNode?.textContent) {
              styleEl.textContent = sheet.ownerNode.textContent
            }
            printDoc.head.appendChild(styleEl)
          }
        } catch (_e) {
          // CORS-blocked sheets are inaccessible via cssRules but are
          // already linked via their href branch above skip silently.
        }
      })

      const printStyle = printDoc.createElement('style')
      printStyle.textContent = `
        body { margin: 0; padding: 16px; }
        .article-toolbar,
        .article-toolbar-wrapper { display: none !important; }
      `
      printDoc.head.appendChild(printStyle)

      const clone = sourceNode.cloneNode(true)
      const toolbarEl = clone.querySelector('.article-toolbar')
      if (toolbarEl) toolbarEl.remove()
      const toolbarWrapper = clone.querySelector('.article-toolbar-wrapper')
      if (toolbarWrapper) toolbarWrapper.remove()

      // Replace all anchor tags with plain text so the PDF has no clickable links
      clone.querySelectorAll('a').forEach((link) => {
        link.replaceWith(document.createTextNode(link.textContent || ''))
      })

      printDoc.body.appendChild(clone)

      const triggerPrint = () => {
        let isClosed = false
        const closePrintWindow = () => {
          if (isClosed || printWindow.closed) return
          isClosed = true
          printWindow.close()
        }

        printWindow.onafterprint = closePrintWindow

        let hasBlurredForPrintDialog = false
        const onBlur = () => {
          hasBlurredForPrintDialog = true
        }
        const onFocus = () => {
          if (hasBlurredForPrintDialog) {
            setTimeout(closePrintWindow, 150)
          }
        }
        printWindow.addEventListener('blur', onBlur, { once: true })
        printWindow.addEventListener('focus', onFocus, { once: true })

        printWindow.focus()
        printWindow.print()
        activityTracker.trackDocumentAction(
          'print',
          'DTAA',
          treatyContent?.slug || treaty?.slug,
          (treatyContent?.title || treaty?.title)?.replace(/<[^>]*>/g, ''),
        )
      }

      Promise.all(styleLoadPromises).finally(() => {
        setTimeout(() => {
          if (printWindow.requestAnimationFrame) {
            printWindow.requestAnimationFrame(triggerPrint)
          } else {
            triggerPrint()
          }
        }, 100)
      })
    }
    // }, [componentRef])
  }, [gtlNotesContent, isAccordionExpanded, treaty, treatyContent])

  useEffect(() => {
    const handlePrintShortcut = (event) => {
      const isPrintShortcut =
        (event.ctrlKey || event.metaKey) && event.key?.toLowerCase() === 'p'
      if (!isPrintShortcut) return

      event.preventDefault()
      event.stopPropagation()
      handlePrint()
    }

    window.addEventListener('keydown', handlePrintShortcut)
    return () => {
      window.removeEventListener('keydown', handlePrintShortcut)
    }
  }, [handlePrint])

  const hasDefinitionTables =
    parsedContent?.querySelectorAll('table.definition').length > 0
  // useEffect will work if you apply proper querySelector, for example you have written find all anchor tags who have target="_blank", but if you try to inspect the article in browser, you won't find the target attribute in anchor tag, maybe becuase of DOMPurify.sanitize()
  // Method 2 Trying html-react-parser -  avoiding potential memory leaks from improper handling of event listeners
  if (loading) {
    return (
      <Box sx={{ padding: 2, margin: 2 }}>
        <Skeleton variant='rectangular' width='100%' height={200} />
        <Skeleton variant='text' width='60%' />
        <Skeleton variant='text' width='80%' />
      </Box>
    )
  }
  // Access Blocked State (402 Response)
  if (accessBlocked) {
    return (
      <UpgradePromptModal
        open={true}
        reason='quota_exhausted'
        quotaResetDate={quotaError?.quotaResetDate || quotaStatus?.quotaResetDate}
        documentType='dtaa'
      />
    )
  }
  if (error) {
    return (
      <Enhanced404Handler
        error={error}
        article={treaty}
        showFullDetails={process.env.REACT_APP_ENVIRONMENT === 'dev'} // Show details only in dev
      />
    )
  }
  return (
    <>
      {/* SEO Component - Only add if not in modal and we have content */}
      {!isInModal && seoData && (
        <SEO
          title={seoData.title}
          description={seoData.description}
          canonical={seoData.canonical}
          keywords={seoData.keywords}
          schemaMarkup={seoData.schemaMarkup}
          publishedDate={seoData?.publishedDate}
          modifiedDate={seoData?.modifiedDate}
          country2Name={seoData.country2Name}
          translationStatus={seoData.translationStatus}
          issueDate={seoData?.issueDate}
          articleType='treaty'
        />
      )}
      {!isInModal && treatyContent && (
        <BreadcrumbComponent
          currentItem={treatyContent}
          itemType='tax-treatie'
          lawInfo={null} // Treaties don't have parent laws
          country={treatyContent.slug?.split('-')[0]}
        />
      )}
      {/* Beta Version Notice */}
      {treatyContent?.betaVersion && (
        <BetaVersionNotice
          show={treatyContent.betaVersion}
          isInModal={isInModal}
        />
      )}
      <Paper
        ref={componentRef}
        sx={{
          padding: 2,
          margin: 2,
          borderRadius: '8px',
          position: 'relative', // Make sure Paper has relative positioning
        }}
      >
        <Box
          display='flex'
          justifyContent='flex-end'
          alignItems='center'
          className='article-toolbar-wrapper'
        >
          <Box className='article-toolbar'>
            {hasDefinitionTables && (
              <Tooltip
                title={
                  sortedContent
                    ? 'Restore the order of definitions back'
                    : 'Sort Definitions Alphabetically'
                }
                arrow
              >
                <IconButton
                  aria-label={
                    sortedContent
                      ? 'Restore the order of definitions back'
                      : 'Sort Definitions Alphabetically'
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    sortedContent ? resetToDefault() : sortDefinitions()
                  }}
                  size={isMobileScreen ? 'small' : 'medium'}
                  sx={sortedContent ? {} : sortButtonStyles}
                >
                  {sortedContent ? (
                    <RestoreRoundedIcon sx={{ color: themeColors.primary }} />
                  ) : (
                    <SortByAlphaRoundedIcon
                      sx={{ color: themeColors.primary, fontSize: 28 }}
                    />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={
                isFeedbackFormOpen
                  ? 'Close feedback form'
                  : 'Report Broken Links in this Treaty'
              }
              arrow
            >
              <IconButton
                aria-label={
                  isFeedbackFormOpen
                    ? 'Close feedback form'
                    : 'Report Broken Links in this Treaty'
                }
                // title={
                //   isFeedbackFormOpen
                //     ? 'Close feedback form'
                //     : 'Report Broken Links in this treaty'
                // }
                onClick={(e) => {
                  e.stopPropagation()
                  handleFeedbackFormToggle()
                }}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                {isFeedbackFormOpen ? (
                  <CloseRoundedIcon color='error' />
                ) : (
                  <ReportRoundedIcon color='error' />
                )}
              </IconButton>
            </Tooltip>
            {process.env.REACT_APP_ENVIRONMENT !== 'prod' && (
              <Tooltip title='Print this treaty' arrow>
                <IconButton
                  aria-label='Print this treaty'
                  onClick={(e) => {
                    e.stopPropagation()
                    handlePrint()
                  }}
                  size={isMobileScreen ? 'small' : 'medium'}
                >
                  <PrintRoundedIcon sx={{ color: themeColors.primary }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title='Share this treaty' arrow>
              <IconButton
                aria-label='Share this treaty'
                onClick={(e) => {
                  e.stopPropagation()
                  handleShare()
                }}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                <ShareRounded sx={{ color: themeColors.primary }} />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                !canModifyDocumentFeatures && !isBookmarked
                  ? 'View-only mode: no active subscription. Upgrade to enable bookmarking.'
                  : isBookmarked
                    ? 'Remove bookmark'
                    : 'Bookmark this DTAA'
              }
              arrow
            >
              <IconButton
                aria-label={
                  isBookmarked ? 'Remove bookmark' : 'Bookmark this DTAA'
                }
                onClick={(e) => {
                  e.stopPropagation()
                  handleBookmark()
                }}
                disabled={bookmarkLoading}
                size={isMobileScreen ? 'small' : 'medium'}
                sx={{ position: 'relative' }}
              >
                {bookmarkLoading ? (
                  <CircularProgress
                    size={isMobileScreen ? 16 : 20}
                    sx={{ color: themeColors.primary }}
                  />
                ) : isBookmarked ? (
                  <BookmarkIcon
                    sx={{
                      color: themeColors.secondary,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                    }}
                  />
                ) : (
                  <BookmarkBorderIcon sx={{ color: themeColors.primary }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title='Mail this treaty' arrow>
              <IconButton
                aria-label='Mail this treaty'
                // title='Mail this treaty'
                onClick={(e) => {
                  e.stopPropagation()
                  handleEmail(treatyContent || treaty)
                }}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                <MailRoundedIcon
                  sx={{ color: themeColors.primary }}
                  // color='primary'
                />
              </IconButton>
            </Tooltip>
            <Tooltip title='Open this treaty in new tab' arrow>
              <IconButton
                aria-label='Open this treaty in new tab'
                // title='Open this treaty in new tab'
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenInNewTab(
                    treatyContent || treaty,
                    `/tax-treaties/${treaty?.slug}`,
                    showSnackbar,
                  )
                }}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                <OpenInNewRoundedIcon
                  sx={{ color: themeColors.primary }}
                  // color='primary'
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <LastUpdatedDisplay
          updatedAt={treatyContent?.updatedAt}
          isInModal={isInModal}
        />
        {isFeedbackFormOpen && (
          <FeedbackForm
            articleId={treatyContent?.slug || treaty.slug}
            onClose={handleFeedbackFormToggle}
            showSnackbar={showSnackbar}
          />
        )}

        {contentHtml ? (
          <AnnotatedArticle
            documentId={treatyContent?.slug || treaty?.slug || 'unknown'}
            documentType='tax-treaty'
            enableAnnotations={true}
            allowAnnotationEdits={canModifyDocumentFeatures}
            readOnlyMessage='This document is in view-only mode. Upgrade or wait for quota reset to add or edit annotations.'
          >
            <Box mt={2} ref={printContentRef}>
              {parseHtmlSanitizeAddTargetToLinks(
                contentHtml,
                searchQuery,
                '/tax-treaties/' + (treatyContent?.slug || treaty?.slug),
                {
                  fullDocumentContent: treatyContent?.content || contentHtml,
                  documentTitle:
                    treatyContent?.title || treaty?.title || 'Tax-Treaty',
                  showSnackbar: showSnackbar,
                  quotaStatus: quotaStatus,
                },
              )}

              {/* MLI content (if available) */}
              {treatyContent?.mliContent && (
                <>
                  <Typography variant='h6' mt={4} mb={2}>
                    MLI Provisions
                  </Typography>
                  <Typography>
                    {parseHtmlSanitizeAddTargetToLinks(
                      treatyContent.mliContent,
                      searchQuery,
                      '/tax-treaties/' + (treatyContent?.slug || treaty?.slug),
                      {
                        fullDocumentContent:
                          treatyContent?.mliContent || contentHtml,
                        documentTitle:
                          treatyContent?.title || treaty?.title || 'Tax-Treaty',
                        showSnackbar: showSnackbar,
                      },
                    )}
                  </Typography>
                </>
              )}
              {/* GTL Notes Accordion */}
              {gtlNotesContent && (
                <Box mt={3}>
                  <Accordion
                    expanded={isAccordionExpanded}
                    onChange={(event, expanded) =>
                      setIsAccordionExpanded(expanded)
                    }
                    aria-label='GTL Notes section'
                    className='gtl-notes-accordion'
                    sx={{
                      '&:before': {
                        display: 'none',
                      },
                      boxShadow: 'none',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '8px !important',
                      '&:not(:last-child)': {
                        borderBottom: 0,
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className='gtl-notes-accordion-summary'
                      sx={{
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '8px',
                        '&.Mui-expanded': {
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0,
                        },
                      }}
                    >
                      <Typography
                        variant='h6'
                        sx={{
                          fontWeight: 600,
                          color: '#43af76',
                        }}
                      >
                        {gtlNotesTitle}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ padding: 2 }}
                      className='gtl-notes-accordion-details'
                    >
                      <Box className='gtl-notes-content'>
                        {parseHtmlSanitizeAddTargetToLinks(
                          gtlNotesContent,
                          searchQuery,
                          '/tax-treaties/' +
                            (treatyContent?.slug || treaty?.slug),
                          {
                            fullDocumentContent:
                              treatyContent?.content || gtlNotesContent,
                            documentTitle:
                              treatyContent?.title ||
                              treaty?.title ||
                              'Tax-Treaty',
                            showSnackbar: showSnackbar,
                          },
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
              {treatyContent?.relatedDocs &&
                treatyContent.relatedDocs.length > 0 && (
                  <RelatedDocuments
                    relatedDocs={treatyContent.relatedDocs}
                    showSnackbar={showSnackbar}
                  />
                )}
            </Box>
          </AnnotatedArticle>
        ) : (
          <Typography>
            We're currently building the most comprehensive tax law repository
            for the GCC region. If you happen to have this particular document,
            we'd be truly grateful if you could share it with us.
            <br />
            Your contribution will not only enhance our database but also help
            the entire community access accurate legal information.
            <br />
            <br />
            📧 Mail us at:{' '}
            <strong>
              <Link
                href='mailto:support@gcctaxlaws.com'
                underline='none'
                sx={{
                  color: '#5948ca',
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                support@gcctaxlaws.com
              </Link>
            </strong>
          </Typography>
        )}
      </Paper>

      {/* Unofficial Translation Notice - outside the Paper component but within the fragment */}
      {treatyContent?.officialTranslation === false && (
        <UnofficialTranslationNotice
          show={!treatyContent?.officialTranslation}
          isInModal={isInModal}
        />
      )}
      {/* Back to Top button - only shown in standalone mode (non-modal), not in modal */}
      {!isInModal && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1000,
          }}
        >
          <Zoom in={showBackToTop}>
            {isMediumScreen ? (
              <Fab
                size='small'
                aria-label='Scroll back to top'
                onClick={scrollToTop}
                sx={{
                  backgroundColor: themeColors.primary,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: themeColors.hover,
                  },
                }}
              >
                <KeyboardArrowUpIcon fontSize='small' />
              </Fab>
            ) : (
              <Fab
                variant='extended'
                size='medium'
                aria-label='Scroll back to top'
                onClick={scrollToTop}
                sx={{
                  backgroundColor: themeColors.primary,
                  color: 'white',
                  '&:hover': { backgroundColor: themeColors.hover },
                  paddingLeft: 2,
                  paddingRight: 2,
                  borderRadius: buttonBorderRadius,
                }}
              >
                <KeyboardArrowUpIcon sx={{ mr: 1 }} />
                <Typography
                  variant='button'
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Back to Top
                </Typography>
              </Fab>
            )}
          </Zoom>
        </Box>
      )}
    </>
  )
}

export default TaxTreatyArticle

